<template>
  <div>
    <SideMenuDrawer v-model="isActive">
      <template #default="{ counterXSpaceClass, counterYSpaceClass }">
        <SideMenuContent
          :list-class="[counterXSpaceClass, counterYSpaceClass]"
          :hide-town-select="hideTownSelect"
          :town="town"
          :town-select-url="townSelectUrl"
          :expert-url="expertUrl"
          :personal-area-url="personalAreaUrl"
          :projects="projects"
          @open-languages="openLanguagesBottomSheet"
          @open-projects="openOurProjectsDialog"
          @open-town="openTownDialog"
          @logout="handleLogout"
          @click-card-info="handleClickCardInfo"
        />
      </template>
    </SideMenuDrawer>

    <SideMenuLanguageSheet
      v-model="isLanguageBottomPannelOpen"
      :languages="availableLanguages"
    />

    <component
      :is="OurProjectsDialogComponent"
      v-if="hasWindow && projects"
      v-model="isActiveOurProjectsDialog"
      :projects="projects"
    />

    <HeaderTown
      v-if="hasWindow"
      v-model="isTownDialogActive"
      hide-trigger
    />

    <LogoutDialog
      v-if="hasWindow"
      v-model="isLogoutDialogActive"
      @confirm="logout"
    />

    <CoreSnackbar />
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  defineAsyncComponent,
  ref,
  toRefs,
} from 'vue'
import SideMenuDrawer from './SideMenuDrawer.vue'
import { ERROR as SNACKBAR_ERROR } from 'src/core/CoreSnackbar/constants'
import dispatchCustomEventGlobally from 'www/utils/withDependences/dispatchCustomEventGlobally'
import CoreSnackbar from 'src/core/CoreSnackbar/CoreSnackbar.vue'
import useSnackbar from 'src/composables/useSnackbar'
import getApiClient from 'src/utils/getApiClient'
import SideMenuContent from 'src/layouts/core-layout/components/CoreHeader/components/mobile/SideMenu/SideMenuContent.vue'
import SideMenuLanguageSheet from 'src/layouts/core-layout/components/CoreHeader/components/mobile/SideMenu/SideMenuLanguageSheet.vue'
import LogoutDialog from 'src/layouts/core-layout/components/CoreHeader/components/mobile/SideMenu/components/LogoutDialog.vue'
import HeaderTown from 'src/layouts/core-layout/components/CoreHeader/components/HeaderTown.vue'
import { SideMenuLanguage } from 'src/layouts/core-layout/components/CoreHeader/components/mobile/SideMenu/SideMenu.types'

const props = withDefaults(defineProps<{
  value?: boolean
  expertUrl?: string
  hideTownSelect?: boolean
  availableLanguages?: SideMenuLanguage[]
  town?: string
  townSelectUrl?: string
  projects?: Record<string, unknown>
  personalAreaUrl?: string
}>(), {
  value: false,
  expertUrl: '',
  hideTownSelect: false,
  availableLanguages: () => [],
  town: '',
  townSelectUrl: '',
  projects: undefined,
  personalAreaUrl: '',
})

const emit = defineEmits(['input'])

const {
  hideTownSelect,
  availableLanguages,
  town,
  townSelectUrl,
  expertUrl,
  projects,
  personalAreaUrl,
} = toRefs(props)

const hasWindow = typeof window !== 'undefined'
const OurProjectsDialogComponent = defineAsyncComponent(
  () => import('src/layouts/core-layout/components/CoreHeader/components/mobile/SideMenu/components/OurProjectsDialog.vue'),
)

const isLanguageBottomPannelOpen = ref(false)
const isActiveOurProjectsDialog = ref(false)
const isTownDialogActive = ref(false)
const isLogoutDialogActive = ref(false)
const snackbar = useSnackbar()

const isActive = computed<boolean>({
  get: () => props.value,
  set: value => emit('input', value),
})

function openLanguagesBottomSheet() {
  isActive.value = false
  isLanguageBottomPannelOpen.value = true
}

function openOurProjectsDialog() {
  isActiveOurProjectsDialog.value = true
}

function openTownDialog() {
  isActive.value = false
  isTownDialogActive.value = true
}

function handleLogout() {
  isLogoutDialogActive.value = true
}

function handleClickCardInfo() {
  isActive.value = false
  dispatchCustomEventGlobally('club-card-info:open')
}

async function logout() {
  if (!hasWindow) {
    return
  }

  try {
    await getApiClient().get('/profile/logout/', { withCredentials: true })
    window.location.assign('/')
  } catch {
    snackbar.show({
      text: SNACKBAR_ERROR.message,
      action: {
        btnText: SNACKBAR_ERROR.actionText,
        onClick: SNACKBAR_ERROR.handleAction,
      },
    })
  }
}
</script>

<style lang="scss" scoped>
@import '~www/themes/doctors/common/vuetify-custom-reset';

::v-deep {
  @include vuetify-custom-reset;
}
</style>
